import { useMemo } from "react";
import {
  ExtractedContract,
  BuildingCheckResponse,
} from "../../types/contract";

interface DuplicateCheckResult {
  shouldShowWarning: boolean;
  contractIdx: number;
  checkResult: BuildingCheckResponse | undefined;
  currentResolutions: { building: "keep" | "update" | null; owner: "keep" | "update" | null };
  dynamicBuildingMatch: "name" | "address" | "both" | null;
  dynamicOwnerMatch: {
    type: "exact" | "suggest" | "none";
    reason?: string;
  };
}

export const useBuildingDuplicateCheck = (
  selectedContract: ExtractedContract | null,
  finalContracts: ExtractedContract[],
  buildingChecks: Map<number, BuildingCheckResponse>,
  conflictResolutions: Map<
    number,
    { building: "keep" | "update" | null; owner: "keep" | "update" | null }
  >
): DuplicateCheckResult | null => {
  return useMemo(() => {
    if (!selectedContract || selectedContract.id === undefined) {
      return null;
    }

    const contractIdx = finalContracts.findIndex(
      (c) =>
        c.property_name === selectedContract.property_name &&
        c.room_name === selectedContract.room_name &&
        c.lessee_name === selectedContract.lessee_name,
    );

    // 早期リターン: buildingChecksに存在しない
    if (!buildingChecks.has(contractIdx)) {
      return null;
    }

    const checkResult = buildingChecks.get(contractIdx);
    // 現在の選択状態を取得（デフォルトは予測ロジックで決定）
    const currentResolutions = conflictResolutions.get(contractIdx) || {
      building: null,
      owner: null,
    };

    if (!checkResult || !checkResult.found || !checkResult.building) {
      return null;
    }

    // 現在の編集内容と既存データを動的に比較（建物情報）
    const currentBuildingName = selectedContract.property_name;
    const currentAddress = selectedContract.property_address?.value;
    const existingBuilding = checkResult.building;

    const nameMatches = existingBuilding.name === currentBuildingName;
    const addressMatches = existingBuilding.address1 === currentAddress;

    let dynamicBuildingMatch: "name" | "address" | "both" | null = null;
    if (nameMatches && addressMatches) dynamicBuildingMatch = "both";
    else if (nameMatches) dynamicBuildingMatch = "name";
    else if (addressMatches) dynamicBuildingMatch = "address";

    // 現在の編集内容と既存データを動的に比較（家主情報）
    let dynamicOwnerMatch: {
      type: "exact" | "suggest" | "none";
      reason?: string;
    } = { type: "none" };

    if (checkResult.owner) {
      const existingOwner = checkResult.owner;
      const newName = selectedContract.owner_name?.value;
      const newKana = selectedContract.owner_name_kana?.value;
      const newPhone = selectedContract.owner_home_phone?.value;
      const newAddress = selectedContract.owner_address1?.value;

      // 完全一致判定
      if (existingOwner.name === newName) {
        dynamicOwnerMatch = { type: "exact" };
      }
      // サジェスト判定: カナ
      else if (newKana && existingOwner.name_kana) {
        const normalizedNewKana = newKana.replace(/\s+/g, "");
        const normalizedExistingKana = existingOwner.name_kana.replace(
          /\s+/g,
          "",
        );
        if (normalizedNewKana === normalizedExistingKana) {
          dynamicOwnerMatch = {
            type: "suggest",
            reason: "カナ",
          };
        }
      }
      // サジェスト判定: 電話番号
      if (dynamicOwnerMatch.type === "none" && newPhone) {
        const normalizedNewPhone = newPhone.replace(/[-\s()（）]/g, "");
        const normalizedHomePhone = existingOwner.home_phone?.replace(
          /[-\s()（）]/g,
          "",
        );
        const normalizedMobilePhone = existingOwner.mobile_phone?.replace(
          /[-\s()（）]/g,
          "",
        );
        if (
          normalizedHomePhone === normalizedNewPhone ||
          normalizedMobilePhone === normalizedNewPhone
        ) {
          dynamicOwnerMatch = {
            type: "suggest",
            reason: "電話番号",
          };
        }
      }
      // サジェスト判定: 住所
      if (
        dynamicOwnerMatch.type === "none" &&
        newAddress &&
        existingOwner.address1 === newAddress
      ) {
        dynamicOwnerMatch = {
          type: "suggest",
          reason: "住所",
        };
      }
    }

    // 一致がなくなった場合は警告を表示しない
    if (!dynamicBuildingMatch) {
      return null;
    }

    return {
      shouldShowWarning: true,
      contractIdx,
      checkResult,
      currentResolutions,
      dynamicBuildingMatch,
      dynamicOwnerMatch,
    };
  }, [selectedContract, finalContracts, buildingChecks, conflictResolutions]);
};